import { useEffect, useRef } from "react";

import { sanitizeModel, type ModelType } from "@/components/agent/ModelDropdown";
import { useCodexEnabled } from "@/hooks/use-codex-enabled";

interface UseLoadPersistedModelOptions {
  /** Load once per key (e.g. `${projectId}|${workspaceId}`); a new key loads again. */
  key: string;
  /** Skip loading entirely (e.g. the model is controlled by a parent). */
  enabled?: boolean;
  /** Returns the persisted model id, or null/undefined when nothing is stored. */
  load: () => Promise<string | null | undefined>;
  /** Receives the persisted model after it is sanitized for this viewer. */
  apply: (model: ModelType) => void;
}

/**
 * Load a persisted agent model (project override / workspace default) exactly once per key, and
 * only after the Codex flag has resolved.
 *
 * Why the wait: `sanitizeModel` drops `codex/…` ids when Codex is disabled for the viewer. While
 * the features call is still in flight `useCodexEnabled()` reports `enabled: false`, so loading the
 * model on mount silently turned a stored `codex/gpt-6-luna` into the Claude default and the next
 * message went to Claude (prod, 2026-11-04 — seen when several editor tabs loaded together).
 */
export function useLoadPersistedModel({ key, enabled = true, load, apply }: UseLoadPersistedModelOptions): void {
  const { enabled: codexEnabled, loading: codexLoading } = useCodexEnabled();
  const loadedKey = useRef<string | null>(null);
  const loadRef = useRef(load);
  const applyRef = useRef(apply);
  loadRef.current = load;
  applyRef.current = apply;

  useEffect(() => {
    if (!enabled || codexLoading || loadedKey.current === key) return;
    loadedKey.current = key;
    let cancelled = false;
    let settled = false;

    loadRef
      .current()
      .then((model) => {
        settled = true;
        if (cancelled || !model) return;
        applyRef.current(sanitizeModel(model, { codexEnabled }));
      })
      .catch((error: unknown) => {
        // Keep the current (default) model; failures are not retried for this key.
        settled = true;
        console.debug("Failed to load persisted model:", error);
      });

    return () => {
      cancelled = true;
      // Torn down before the load finished (StrictMode remount, deps change): allow a new run.
      // Once settled, never reload for the same key — that would overwrite the user's own pick.
      if (!settled && loadedKey.current === key) loadedKey.current = null;
    };
  }, [key, enabled, codexLoading, codexEnabled]);
}
